<template>
	<div class="min-h-screen bg-gray-50 font-sans">
		<!-- Header -->
		<header class="bg-white border-b border-gray-200 px-3 py-4">
			<div class="w-full flex justify-between items-center flex-wrap gap-2">
				<div>
					<h1 class="text-2xl font-semibold text-gray-900 m-0">Currencies</h1>
					<p class="text-sm text-gray-500 mt-1 mb-0">Manage currencies and their display settings</p>
				</div>
				<button
					type="button"
					class="inline-flex items-center gap-2 py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-orange-6"
					@click="openCreateModal"
				>
					<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
					</svg>
					Create New
				</button>
			</div>
		</header>

		<!-- Main Content -->
		<main class="w-full p-3">
			<!-- Card -->
			<div class="bg-white rounded-xl shadow-sm overflow-hidden">
				<!-- Card Header -->
				<div class="p-4 px-6 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
					<div class="flex items-center gap-3 flex-wrap">
						<h2 class="text-lg font-semibold text-gray-900 m-0">All Currencies</h2>
						<span class="inline-flex items-center py-1 px-2.5 text-xs font-medium bg-orange-2 text-orange-7 rounded-full">
							{{ totalRows }} total
						</span>
					</div>
					<div class="flex items-center gap-3 flex-wrap">
						<!-- Search -->
						<div class="relative">
							<svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
							</svg>
							<input
								v-model="searchQuery"
								type="text"
								placeholder="Search currencies..."
								class="py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm w-56 outline-none transition-colors focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
							>
						</div>
						<!-- Refresh Button -->
						<button
							type="button"
							class="inline-flex items-center gap-2 py-2 px-4 border border-gray-300 bg-white rounded-lg text-sm font-medium text-gray-700 cursor-pointer transition-colors hover:bg-gray-50"
							@click="refreshData"
						>
							<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
							</svg>
							Refresh
						</button>
					</div>
				</div>

				<!-- Table -->
				<div class="overflow-x-auto">
					<!-- Loading State -->
					<div v-if="isTableLoading" class="flex justify-center items-center py-16 px-4">
						<svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
							<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
							<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
						</svg>
						<span class="ml-3 text-gray-500 text-sm">Loading currencies...</span>
					</div>

					<!-- Empty State -->
					<div v-else-if="filteredData.length === 0" class="text-center py-16 px-4">
						<svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
							<path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
						</svg>
						<h3 class="text-base font-medium text-gray-700 m-0 mb-1">No currencies found</h3>
						<p class="text-sm text-gray-500 m-0">Try adjusting your search or create a new currency.</p>
					</div>

					<!-- Data Table -->
					<table v-else class="w-full border-collapse">
						<thead>
							<tr class="bg-gray-50">
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
								<th class="py-3 px-6 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Sign</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Prefix</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Suffix</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Separator</th>
								<th class="py-3 px-6 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="item in paginatedData"
								:key="item.id"
								class="border-b border-gray-200 transition-colors hover:bg-gray-50"
							>
								<td class="py-3.5 px-6 text-sm text-gray-500 font-mono">{{ item.id }}</td>
								<td class="py-3.5 px-6 text-sm font-medium text-gray-900">{{ item.name || '-' }}</td>
								<td class="py-3.5 px-6 text-base font-semibold text-orange-7 text-center">{{ item.sign || '-' }}</td>
								<td class="py-3.5 px-6 text-sm text-gray-500">{{ item.prefix || '-' }}</td>
								<td class="py-3.5 px-6 text-sm text-gray-500">{{ item.suffix || '-' }}</td>
								<td class="py-3.5 px-6 text-sm text-gray-500">{{ item.separator || '-' }}</td>
								<td class="py-3.5 px-6 text-right">
									<div class="flex justify-end gap-2">
										<button
											type="button"
											class="inline-flex items-center py-1.5 px-3 rounded-md text-xs font-medium text-orange-7 bg-transparent border-none cursor-pointer transition-colors hover:bg-orange-2"
											@click="editCurrency(item)"
										>
											<svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
											</svg>
											Edit
										</button>
										<button
											type="button"
											class="inline-flex items-center py-1.5 px-3 rounded-md text-xs font-medium text-red-600 bg-transparent border-none cursor-pointer transition-colors hover:bg-red-50"
											@click="confirmDelete(item)"
										>
											<svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
											</svg>
											Delete
										</button>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- Pagination -->
				<div v-if="totalPages > 1" class="p-4 px-6 border-t border-gray-200 flex justify-between items-center flex-wrap gap-4">
					<p class="text-sm text-gray-500 m-0">
						Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredData.length) }} of {{ filteredData.length }} results
					</p>
					<div class="flex gap-1">
						<button
							type="button"
							:disabled="currentPage === 1"
							class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
							@click="currentPage > 1 && currentPage--"
						>
							Previous
						</button>
						<button
							v-for="page in visiblePages"
							:key="page"
							type="button"
							class="py-2 px-3 border rounded-md text-sm cursor-pointer transition-colors"
							:class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
							@click="currentPage = page"
						>
							{{ page }}
						</button>
						<button
							type="button"
							:disabled="currentPage === totalPages"
							class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
							@click="currentPage < totalPages && currentPage++"
						>
							Next
						</button>
					</div>
				</div>
			</div>
		</main>

		<!-- Create Modal -->
		<div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center">
			<div class="absolute inset-0 bg-black/50" @click="closeModal"></div>
			<div class="relative bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto m-4">
				<!-- Modal Header -->
				<div class="py-5 px-6 border-b border-gray-200 flex justify-between items-center">
					<h3 class="text-lg font-semibold text-gray-900 m-0">Create New Currency</h3>
					<button
						type="button"
						class="text-gray-500 bg-transparent border-none cursor-pointer p-1 hover:text-gray-700"
						@click="closeModal"
					>
						<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>
				<!-- Modal Body -->
				<div class="p-6">
					<div class="flex flex-col gap-4">
						<!-- Currency Select -->
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1.5">Select Currency *</label>
							<select
								v-model="selectedCurrencyCode"
								class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none bg-white focus:border-orange-7"
							>
								<option value="">Choose a currency...</option>
								<option v-for="curr in isoCurrencies" :key="curr.code" :value="curr.code">
									{{ curr.code }} - {{ curr.name }} ({{ curr.symbol }})
								</option>
							</select>
						</div>
					</div>
				</div>
				<!-- Modal Footer -->
				<div class="p-4 px-6 border-t border-gray-200 flex justify-end gap-3">
					<button
						type="button"
						class="py-2 px-4 border border-gray-300 bg-white rounded-lg text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50"
						@click="closeModal"
					>
						Cancel
					</button>
					<button
						type="button"
						:disabled="isSaving || !selectedCurrencyCode"
						class="py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-6"
						@click="createCurrency"
					>
						{{ isSaving ? 'Creating...' : 'Create Currency' }}
					</button>
				</div>
			</div>
		</div>

		<!-- Toast Notification -->
		<div v-if="toastMessage" class="fixed bottom-6 right-6 z-50">
			<div
				class="flex items-center gap-3 py-3 px-4 rounded-lg shadow-lg"
				:class="toastType === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'"
			>
				<svg v-if="toastType === 'success'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
					<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
				</svg>
				<svg v-else class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
					<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
				</svg>
				<span class="text-sm font-medium">{{ toastMessage }}</span>
			</div>
		</div>
	</div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import Swal from 'sweetalert2';
import CurrencyService from 'services/CurrencyService';
import isoCurrencyData from '@/data/iso-currency.json';

export default defineComponent({
	name: 'CurrenciesPage',
	setup() {
		const router = useRouter();

		const allData = ref([]);
		const isTableLoading = ref(true);
		const searchQuery = ref('');
		const currentPage = ref(1);
		const pageSize = 20;
		const showModal = ref(false);
		const selectedCurrencyCode = ref('');
		const isSaving = ref(false);
		const toastMessage = ref('');
		const toastType = ref('success');

		const isoCurrencies = computed(() => {
			return isoCurrencyData.sort((a, b) => a.code.localeCompare(b.code));
		});

		const filteredData = computed(() => {
			if (!searchQuery.value.trim()) return allData.value;
			const query = searchQuery.value.toLowerCase();
			return allData.value.filter(item =>
				item.name?.toLowerCase().includes(query) ||
				item.sign?.toLowerCase().includes(query) ||
				String(item.id).includes(query)
			);
		});

		const totalRows = computed(() => filteredData.value.length);
		const totalPages = computed(() => Math.ceil(filteredData.value.length / pageSize));

		const paginatedData = computed(() => {
			const start = (currentPage.value - 1) * pageSize;
			return filteredData.value.slice(start, start + pageSize);
		});

		const visiblePages = computed(() => {
			const pages = [];
			const maxVisible = 5;
			let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
			let end = Math.min(totalPages.value, start + maxVisible - 1);
			if (end - start + 1 < maxVisible) {
				start = Math.max(1, end - maxVisible + 1);
			}
			for (let i = start; i <= end; i++) {
				pages.push(i);
			}
			return pages;
		});

		watch(searchQuery, () => {
			currentPage.value = 1;
		});

		const loadData = async () => {
			isTableLoading.value = true;
			try {
				const params = new URLSearchParams({ limit: '0' });
				const response = await CurrencyService.getAll(params);
				allData.value = response;
			} catch (error) {
				console.error('Failed to load currencies:', error);
				showToast('Failed to load currencies', 'error');
			} finally {
				isTableLoading.value = false;
			}
		};

		const refreshData = () => {
			loadData();
		};

		const openCreateModal = () => {
			selectedCurrencyCode.value = '';
			showModal.value = true;
		};

		const closeModal = () => {
			showModal.value = false;
			selectedCurrencyCode.value = '';
		};

		const createCurrency = async () => {
			if (!selectedCurrencyCode.value) return;

			isSaving.value = true;
			try {
				const selectedCurrency = isoCurrencies.value.find(c => c.code === selectedCurrencyCode.value);
				if (!selectedCurrency) {
					throw new Error('Currency not found');
				}

				await CurrencyService.create({
					name: selectedCurrency.code,
					sign: selectedCurrency.symbol,
					prefix: selectedCurrency.symbol,
					suffix: '',
					separator: '.',
				});

				closeModal();
				showToast('Currency created successfully', 'success');
				loadData();
			} catch (error) {
				console.error('Failed to create currency:', error);
				showToast(error.message || 'Failed to create currency', 'error');
			} finally {
				isSaving.value = false;
			}
		};

		const editCurrency = (item) => {
			router.push(`/currency/${item.id}`);
		};

		const confirmDelete = async (item) => {
			const result = await Swal.fire({
				title: 'Delete Currency?',
				text: `Are you sure you want to delete "${item.name}"? This action cannot be undone.`,
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#dc2626',
				cancelButtonColor: '#6b7280',
				confirmButtonText: 'Yes, delete it',
				cancelButtonText: 'Cancel',
			});

			if (result.isConfirmed) {
				try {
					await CurrencyService.delete(item.id);
					allData.value = allData.value.filter(i => i.id !== item.id);
					showToast('Currency deleted successfully', 'success');
				} catch (error) {
					showToast(error.message || 'Failed to delete currency', 'error');
				}
			}
		};

		const showToast = (message, type = 'success') => {
			toastMessage.value = message;
			toastType.value = type;
			setTimeout(() => {
				toastMessage.value = '';
			}, 3000);
		};

		onMounted(() => {
			loadData();
		});

		return {
			allData,
			isTableLoading,
			searchQuery,
			currentPage,
			pageSize,
			showModal,
			selectedCurrencyCode,
			isSaving,
			toastMessage,
			toastType,
			isoCurrencies,
			filteredData,
			totalRows,
			totalPages,
			paginatedData,
			visiblePages,
			refreshData,
			openCreateModal,
			closeModal,
			createCurrency,
			editCurrency,
			confirmDelete,
		};
	},
});
</script>
